import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateApprovalHistory } from '../ApprovalHistory/useApprovalHistoryQuery';
import { invalidateRegEdSubmissions } from './useRegEdSubmissionsQuery';

/**
 * Supersedes the rejected round and raises a fresh one for the same approver, keeping the revision on
 * the campaign instead of in a new document.
 *
 * Sequence is load-bearing and unenforced: save the revised content first — this snapshots the
 * campaign body as the round's reviewed content — then regenerate the compliance PDF, since the round
 * is raised without one and nothing reaches the provider until it exists.
 */
export const useRegEdEmailReviseMutation = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (campaign: Api.ICampaign) => void;
} = {}) => {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			campaignId,
			impersonationContext,
		}: {
			campaignId: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.ICampaign>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `email/${encodeURIComponent(campaignId)}/revise`,
				}),
				'POST'
			),
		onError,
		onSuccess: (campaign, { campaignId }) => {
			invalidateRegEdSubmissions(campaignId);
			invalidateApprovalHistory({ resourceId: campaignId, resourceType: 'email' });
			onSuccess?.(campaign);
		},
	});
};
